// src/pages/InterviewAssistant/InformationCredibility.tsx
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, BarChart2, Award, Eye, MessageCircle, Brain, Activity
} from 'lucide-react';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip
} from 'recharts';

// ------------------- шкалы и чек-листы -------------------
const scales = [
  {
    title: 'Невербальные сигналы',
    id: 'nonverbal',
    description: 'Оцените поведенческие индикаторы достоверности',
    items: [
      { id: 'nv1', text: 'Поддерживает зрительный контакт (не избегает, но и не сверлит)' },
      { id: 'nv2', text: 'Поза открытая, нет скрещённых рук или ног' },
      { id: 'nv3', text: 'Жестикуляция естественная, синхронизирована с речью' },
      { id: 'nv4', text: 'Отсутствуют прикосновения к лицу, почёсывания, потирания носа' },
      { id: 'nv5', text: 'Дыхание ровное, нет признаков учащения или задержки' },
      { id: 'nv6', text: 'Голос уверенный, тембр не меняется резко' },
      { id: 'nv7', text: 'Микровыражения лица соответствуют содержанию речи' },
      { id: 'nv8', text: 'Нет микропауз или неестественных пауз перед ответом' }
    ]
  },
  {
    title: 'Вербальные индикаторы',
    id: 'verbal',
    description: 'Оцените языковые паттерны и структуру ответов',
    items: [
      { id: 'vb1', text: 'Ответы конкретны, без чрезмерных обобщений' },
      { id: 'vb2', text: 'Использует сенсорные детали (звуки, запахи, ощущения)' },
      { id: 'vb3', text: 'Соблюдается логическая последовательность событий' },
      { id: 'vb4', text: 'Отсутствуют противоречия в повторяющихся вопросах' },
      { id: 'vb5', text: 'Не использует шаблонные фразы или клише' },
      { id: 'vb6', text: 'Слова-паразиты и запинки не увеличиваются при сложных вопросах' },
      { id: 'vb7', text: 'Речь эмоционально окрашена адекватно контексту' },
      { id: 'vb8', text: 'Нет избыточной детализации, которая отвлекает от главного' }
    ]
  },
  {
    title: 'Логическая согласованность',
    id: 'logic',
    description: 'Проверка внутренней непротиворечивости рассказа',
    items: [
      { id: 'lg1', text: 'Все даты и временные промежутки стыкуются' },
      { id: 'lg2', text: 'Участники событий упоминаются последовательно' },
      { id: 'lg3', text: 'Мотивация описываемых действий объяснима' },
      { id: 'lg4', text: 'При повторном пересказе детали не меняются' },
      { id: 'lg5', text: 'Реакции на одни и те же контрольные вопросы одинаковы' },
      { id: 'lg6', text: 'Косвенные утверждения подтверждают основные' },
      { id: 'lg7', text: 'Объяснения естественны без притянутых оправданий' },
      { id: 'lg8', text: 'Нет нарушения причинно-следственных связей' }
    ]
  },
  {
    title: 'Методика FACTOR',
    id: 'factor',
    description: 'F – Feelings, A – Action, C – Context, T – Timeline, O – Outcome, R – Reflection',
    items: [
      { id: 'fa1', text: 'Описывает свои чувства в момент события (Feelings)' },
      { id: 'fa2', text: 'Чётко формулирует свои действия (Action)' },
      { id: 'fa3', text: 'Описывает контекст и обстановку (Context)' },
      { id: 'fa4', text: 'Временные рамки реалистичны и последовательны (Timeline)' },
      { id: 'fa5', text: 'Указывает конкретный результат / исход (Outcome)' },
      { id: 'fa6', text: 'Демонстрирует рефлексию / извлечённые уроки (Reflection)' },
      { id: 'fa7', text: 'Все компоненты FACTOR присутствуют в рассказе' },
      { id: 'fa8', text: 'Рассказ звучит правдоподобно и целостно' }
    ]
  }
];

const interpretation: Record<string, { low: string; medium: string; high: string }> = {
  'Невербальные сигналы': {
    low: 'Невербальные сигналы могут указывать на стресс или недостоверность. Рекомендуется перепроверить информацию с помощью уточняющих вопросов.',
    medium: 'Присутствуют как индикаторы достоверности, так и сигналы напряжения. Стоит углубиться в отдельные темы.',
    high: 'Невербальное поведение в целом соответствует искреннему рассказу.'
  },
  'Вербальные индикаторы': {
    low: 'В речи много шаблонов и противоречий. Следует задать контрольные вопросы.',
    medium: 'Речь содержит конкретику, но иногда скатывается в обобщения. Требуется уточнение.',
    high: 'Вербальные паттерны указывают на высокую достоверность.'
  },
  'Логическая согласованность': {
    low: 'Рассказ содержит логические дыры и противоречия. Вероятно, информация не полностью достоверна.',
    medium: 'Большинство фактов согласуются, но есть мелкие нестыковки.',
    high: 'Рассказ логичен, последователен и не содержит противоречий.'
  },
  'Методика FACTOR': {
    low: 'Рассказу не хватает ключевых компонентов FACTOR. Рекомендуется дополнительная проверка.',
    medium: 'Присутствуют некоторые компоненты модели, но нет полноты картины.',
    high: 'Все элементы FACTOR выражены ярко, что свидетельствует о высоком качестве самопрезентации.'
  }
};

// ------------------- компонент -------------------
const InformationCredibility: React.FC = () => {
  const navigate = useNavigate();
  const [currentScaleIndex, setCurrentScaleIndex] = useState(0);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [submitted, setSubmitted] = useState(false);

  const currentScale = scales[currentScaleIndex];

  const handleScoreChange = (itemId: string, value: number) => {
    setScores(prev => ({ ...prev, [itemId]: value }));
  };

  const goNext = () => {
    if (currentScaleIndex < scales.length - 1) {
      setCurrentScaleIndex(prev => prev + 1);
    }
  };

  const goPrev = () => {
    if (currentScaleIndex > 0) {
      setCurrentScaleIndex(prev => prev - 1);
    }
  };

  const allItems = useMemo(() => scales.flatMap(s => s.items), []);
  const allAnswered = allItems.every(item => scores[item.id] !== undefined);

  const handleSubmit = () => setSubmitted(true);

  const scoresByScale = useMemo(() => {
    return scales.map(scale => {
      const total = scale.items.reduce((sum, item) => sum + (scores[item.id] || 0), 0);
      const max = scale.items.length * 5;
      const percentage = Math.round((total / max) * 100);
      return { title: scale.title, percentage };
    });
  }, [scores]);

  const radarData = scoresByScale.map(s => ({
    scale: s.title,
    score: s.percentage,
    fullMark: 100,
  }));

  const overallCredibility = Math.round(scoresByScale.reduce((sum, s) => sum + s.percentage, 0) / scoresByScale.length);

  // ---------- страница результатов ----------
  if (submitted) {
    return (
      <div style={styles.container}>
        <div style={styles.bgBlur1} />
        <div style={styles.bgBlur2} />

        <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
          <ArrowLeft size={18} />
          <span>К помощнику интервью</span>
        </button>

        <div style={styles.header}>
          <h1 style={styles.title}>Оценка достоверности информации</h1>
          <p style={styles.subtitle}>Сводная оценка по всем методикам</p>
        </div>

        <div style={styles.overallCard}>
          <h2 style={{ textAlign: 'center', color: overallCredibility >= 70 ? '#2ecc71' : overallCredibility >= 40 ? '#f39c12' : '#e74c3c' }}>
            {overallCredibility}% общей достоверности
          </h2>
        </div>

        <div style={styles.resultsGrid}>
          <div style={styles.resultCard}>
            <h3 style={{ ...styles.chartTitle, color: '#1a1a2e' }}><BarChart2 size={18} /> Профиль по методикам</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData} outerRadius={100}>
                <PolarGrid strokeDasharray="3 3" />
                <PolarAngleAxis dataKey="scale" tick={{ fontSize: 11 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
                <Radar isAnimationActive={false} name="Достоверность" dataKey="score" stroke="#2575fc" fill="#2575fc" fillOpacity={0.2} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div style={styles.resultCard}>
            <h3 style={{ ...styles.chartTitle, color: '#1a1a2e' }}><Award size={18} /> Детализация</h3>
            {scoresByScale.map(s => {
              const interp = interpretation[s.title];
              const level = s.percentage >= 70 ? 'high' : s.percentage >= 40 ? 'medium' : 'low';
              const color = level === 'high' ? '#2ecc71' : level === 'medium' ? '#f39c12' : '#e74c3c';
              return (
                <div key={s.title} style={styles.scaleRow}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong>{s.title}</strong>
                    <span style={{ color, fontWeight: 600 }}>{s.percentage}%</span>
                  </div>
                  <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${s.percentage}%`, background: color }} />
                  </div>
                  <p style={styles.interpretationText}>
                    {level === 'high' && interp.high}
                    {level === 'medium' && interp.medium}
                    {level === 'low' && interp.low}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => { setSubmitted(false); setScores({}); setCurrentScaleIndex(0); }}
          style={styles.resetButton}
        >
          Пройти заново
        </button>
      </div>
    );
  }

  // ---------- чек-листы ----------
  return (
    <div style={styles.container}>
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />

      <button onClick={() => navigate('/app/interview-assistant')} style={styles.backButton}>
        <ArrowLeft size={18} />
        <span>К помощнику интервью</span>
      </button>

      <div style={styles.header}>
        <h1 style={styles.title}>Оценка достоверности информации</h1>
        <p style={styles.subtitle}>
          Оцените поведенческие и вербальные индикаторы достоверности ответов кандидата.
        </p>
      </div>

      <div style={styles.progressIndicator}>
        {scales.map((_, idx) => (
          <div
            key={idx}
            style={{
              ...styles.dot,
              background: idx <= currentScaleIndex ? '#2575fc' : '#e0e0e0',
            }}
          />
        ))}
      </div>

      <div style={styles.card}>
        <h2 style={styles.scaleTitle}>
          {currentScale.id === 'nonverbal' && <Eye size={20} color="#2575fc" />}
          {currentScale.id === 'verbal' && <MessageCircle size={20} color="#6a11cb" />}
          {currentScale.id === 'logic' && <Brain size={20} color="#e67e22" />}
          {currentScale.id === 'factor' && <Activity size={20} color="#2ecc71" />}
          {currentScale.title}
        </h2>
        <p style={styles.scaleDesc}>{currentScale.description}</p>

        {currentScale.items.map(item => (
          <div key={item.id} style={styles.itemRow}>
            <p style={styles.itemText}>{item.text}</p>
            <div style={styles.rateButtons}>
              {[1, 2, 3, 4, 5].map(val => (
                <button
                  key={val}
                  style={{
                    ...styles.rateButton,
                    background: scores[item.id] === val ? 'linear-gradient(135deg, #2575fc, #1a5fde)' : 'rgba(255,255,255,0.5)',
                    color: scores[item.id] === val ? 'white' : '#1a1a2e',
                    border: scores[item.id] === val ? 'none' : '1px solid rgba(0,0,0,0.1)',
                  }}
                  onClick={() => handleScoreChange(item.id, val)}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>
        ))}

        <div style={styles.navButtons}>
          <button onClick={goPrev} disabled={currentScaleIndex === 0} style={styles.navButton}>
            Назад
          </button>
          <span style={{ color: '#666' }}>
            {currentScaleIndex + 1} / {scales.length}
          </span>
          {currentScaleIndex < scales.length - 1 ? (
            <button onClick={goNext} style={styles.navButton}>Далее</button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!allAnswered}
              style={{
                ...styles.navButton,
                background: allAnswered ? 'linear-gradient(135deg, #2ecc71, #27ae60)' : '#ccc',
                color: 'white',
                border: 'none',
              }}
            >
              Завершить и получить результат
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------- стили -------------------
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '80px 20px 20px 20px',
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 100%)',
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    position: 'relative',
    overflowX: 'clip',                     // вместо 'hidden', чтобы не создавать контекст скролла
    willChange: 'transform',              // ускоряет композитинг
    transform: 'translateZ(0)',           // создаёт отдельный слой

  },
  bgBlur1: {
    position: 'absolute', top: '-10%', left: '-5%', width: 600, height: 600,
    background: 'radial-gradient(circle, rgba(106,17,203,0.12) 0%, rgba(106,17,203,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  bgBlur2: {
    position: 'absolute', bottom: '-10%', right: '-5%', width: 700, height: 700,
    background: 'radial-gradient(circle, rgba(37,117,252,0.12) 0%, rgba(37,117,252,0) 70%)',
    borderRadius: '50%', filter: 'blur(60px)', zIndex: 0,
  },
  backButton: {
    position: 'absolute', top: 24, left: 24,
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: 40,
    padding: '10px 20px', fontSize: 15, fontWeight: 500, color: '#2575fc',
    cursor: 'pointer', transition: 'all 0.2s ease', zIndex: 10, display: 'inline-flex', alignItems: 'center', gap: 8,
  },
  header: { textAlign: 'center', marginBottom: 32, position: 'relative', zIndex: 1 },
  title: {
    fontSize: 36, fontWeight: 700,
    background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: '0 0 8px',
  },
  subtitle: { fontSize: 16, color: '#666', margin: 0 },
  progressIndicator: { display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24, position: 'relative', zIndex: 1 },
  dot: { width: 12, height: 12, borderRadius: '50%', transition: 'background 0.3s' },
  card: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: '40px 32px', border: '1px solid rgba(0,0,0,0.06)',
    maxWidth: 800, margin: '0 auto', position: 'relative', zIndex: 1,
  },
  scaleTitle: { fontSize: 24, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, color: '#1a1a2e' },
  scaleDesc: { fontSize: 15, color: '#4a4a68', marginBottom: 28 },
  itemRow: { marginBottom: 24 },
  itemText: { fontSize: 16, fontWeight: 500, marginBottom: 10, color: '#1a1a2e' },
  rateButtons: { display: 'flex', gap: 8 },
  rateButton: {
    width: 44, height: 44, borderRadius: 22, border: '1px solid rgba(0,0,0,0.1)',
    cursor: 'pointer', fontSize: 16, fontWeight: 600, transition: 'all 0.2s',
  },
  navButtons: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 32 },
  navButton: {
    padding: '10px 24px', borderRadius: 30, border: '1px solid #2575fc', background: 'transparent',
    color: '#2575fc', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s',
  },
  overallCard: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: 24, maxWidth: 400, margin: '0 auto 24px', textAlign: 'center',
    border: '1px solid rgba(0,0,0,0.06)',
  },
  resultsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: 28, maxWidth: 1100, margin: '0 auto', position: 'relative', zIndex: 1 },
  resultCard: {
    background: 'rgba(255,255,255,0.5)', borderRadius: 32,
    padding: 32, border: '1px solid rgba(0,0,0,0.06)',
  },
  chartTitle: { fontSize: 20, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 },
  scaleRow: { marginBottom: 20 },
  progressBar: { height: 8, background: '#e0e0e0', borderRadius: 4, marginTop: 6, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 4, transition: 'width 0.3s' },
  interpretationText: { fontSize: 13, color: '#555', marginTop: 6, lineHeight: 1.5 },
  resetButton: {
    marginTop: 40, display: 'block', marginLeft: 'auto', marginRight: 'auto',
    padding: '12px 28px', borderRadius: 30, background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600,
  },
};

export default InformationCredibility;